import { ArrowForwardOutlined, CheckCircleOutlined, ExpandLessOutlined, ExpandMoreOutlined, RemoveOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useId, useState } from 'react';
import { ListRow, StatusBadge, designTokens } from '../../../design-system';
import { gettingStartedAbsenceCheckDestination, gettingStartedCheckCountUnit, gettingStartedCheckDescription, gettingStartedCheckLabel, gettingStartedCheckOptionalLabel, type GettingStartedCheck, type GettingStartedDestination, type GettingStartedLink } from '../model/gettingStarted';

export function AbsencePreparationChecklist({ checks, links, disabled, onOpen, onStartSetup }: {
  checks: GettingStartedCheck[];
  links: GettingStartedLink[];
  disabled: boolean;
  onOpen: (destination: GettingStartedDestination) => void;
  onStartSetup?: () => void;
}) {
  const [showSettings, setShowSettings] = useState(false);
  const settingsId = useId();
  const problemDestinations = new Set(checks.filter((check) => !check.ready).map(gettingStartedAbsenceCheckDestination));
  const otherLinks = links.filter((link) => !problemDestinations.has(link.destination));

  return <Stack spacing={1}>
    <Stack component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
      {checks.map((check) => {
        const optionalLabel = gettingStartedCheckOptionalLabel(check);
        const description = gettingStartedCheckDescription(check);
        const link = !check.ready ? links.find((entry) => entry.destination === gettingStartedAbsenceCheckDestination(check)) : undefined;
        return <Box key={check.id} component="li">
          <ListRow
            label={gettingStartedCheckLabel(check)}
            description={description ? <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
              <span>{description}</span>
              {link ? <Button variant="text" size="small" endIcon={<ArrowForwardOutlined />} disabled={disabled} onClick={() => onOpen(link.destination)}>{link.label}</Button> : null}
            </Stack> : undefined}
            density="compact"
            inset="none"
            icon={optionalLabel ? <RemoveOutlined fontSize="small" color="action" /> : check.ready ? <CheckCircleOutlined fontSize="small" color="success" /> : <WarningAmberOutlined fontSize="small" color="warning" />}
            trailing={optionalLabel ? <StatusBadge label={optionalLabel} tone="neutral" /> : check.count !== undefined
              ? <Typography variant="body2" sx={{ color: check.ready ? designTokens.color.secondaryText : designTokens.color.warningDark }}>{check.count.toLocaleString()}{gettingStartedCheckCountUnit(check)}</Typography>
              : <StatusBadge label={check.ready ? '준비됨' : '확인 필요'} tone={check.ready ? 'success' : 'warning'} />}
          />
        </Box>;
      })}
    </Stack>
    {otherLinks.length ? <Box>
      <Button variant="text" color="inherit" aria-label="휴가 설정 바로가기" aria-expanded={showSettings} aria-controls={settingsId} endIcon={showSettings ? <ExpandLessOutlined /> : <ExpandMoreOutlined />} disabled={disabled} onClick={() => setShowSettings((current) => !current)}>설정 바로가기</Button>
      <Stack id={settingsId} role="group" aria-label="휴가 설정 바로가기 목록" direction="row" spacing={1} useFlexGap hidden={!showSettings} sx={{ display: showSettings ? 'flex' : 'none', flexWrap: 'wrap', mt: 1 }}>
        {otherLinks.map((link) => <Button key={link.destination} variant="weak" color="inherit" endIcon={<ArrowForwardOutlined />} disabled={disabled} onClick={() => onOpen(link.destination)}>{link.label}</Button>)}
      </Stack>
    </Box> : null}
    {onStartSetup ? <Box><Button variant="text" disabled={disabled} endIcon={<ArrowForwardOutlined />} onClick={onStartSetup}>휴가 설정 도우미</Button></Box> : null}
  </Stack>;
}
